import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { registerUser, getActiveStory, getGameStory, getHotelById, checkUserActiveStatus, getUserByRoomNumber } from "../utils/api";

import logoImage from "../assets/images/ana-intercontinental-logo.png";
import beachBackground from "../assets/images/beach-background.png";
import animeCharacter from "../assets/images/anime-character.png";

const Home = () => {
	const [isDisabled, setIsDisabled] = useState(true);
	const [roomNumber, setRoomNumber] = useState("");
	const [guestName, setGuestName] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [activeStory, setActiveStory] = useState(null);
	const [currentStory, setCurrentStory] = useState(null);
	const [storyLoading, setStoryLoading] = useState(true);
	const [hotelInfo, setHotelInfo] = useState(null);
	const [userId, setUserId] = useState(null);
	const [waitingApproval, setWaitingApproval] = useState(false);
	
	const navigate = useNavigate();
	
	// Initialize and check for active story on component mount
	useEffect(() => {
		// Check if we have a story ID from QR code scanning
		const storyId = localStorage.getItem('storyId');
		if (!storyId) {
			// If no story ID, redirect back to welcome page
			navigate('/');
			return;
		}
		
		// Fetch the current story information
		getGameStory(storyId)
			.then(response => {
				if (response.success && response.data) {
					setCurrentStory(response.data);
					console.log("Current story loaded:", response.data);
				} else {
					console.error("Failed to load story:", response.message);
				}
			})
			.catch(err => {
				console.error("Failed to fetch story:", err);
			})
			.finally(() => {
				setStoryLoading(false);
			});
		
		// Check for active story
		getActiveStory()
			.then(response => {
				if (response.success && response.data) {
					setActiveStory(response.data);
				} else {
					console.error("No active story found:", response.message);
				}
			})
			.catch(err => {
				console.error("Failed to fetch active story:", err);
			});
		
		// Check if we have a hotel ID from QR code scanning
		const hotelId = localStorage.getItem('hotelId');
		if (hotelId) {
			// If we have a hotel ID, fetch hotel details
			getHotelById(hotelId)
				.then(response => {
					if (response.success && response.data) {
						setHotelInfo(response.data);
					}
				})
				.catch(err => {
					console.error("Failed to fetch hotel details:", err);
				});
		}
	}, [navigate]);

	// Input validation
	useEffect(() => {
		if (roomNumber.trim() !== "" && guestName.trim() !== "") {
			setIsDisabled(false);
		} else {
			setIsDisabled(true);
		}
	}, [roomNumber, guestName]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (isDisabled) return;
		
		// Check if active story exists
		if (!activeStory) {
			setError("アクティブなゲームがありません。管理者に連絡してください。");
			return;
		}
		
		setIsLoading(true);
		setError("");
		
		try {
			console.log("Checking for existing user with room number:", roomNumber);
			// First check if a user with this room number already exists
			const existingUserResponse = await getUserByRoomNumber(roomNumber);
			
			if (existingUserResponse.success && existingUserResponse.data && existingUserResponse.data.length > 0) {
				console.log("Found existing user:", existingUserResponse.data[0]);
				const existingUser = existingUserResponse.data[0];
				
				// Store user data
				localStorage.setItem("userId", existingUser._id);
				localStorage.setItem("roomNumber", roomNumber);
				localStorage.setItem("guestName", guestName);
				
				// Store storyId - use the one from localStorage (from QR scan)
				const storyId = localStorage.getItem("storyId");
				
				// Check if user is already approved
				const statusResponse = await checkUserActiveStatus(existingUser._id);
				
				if (statusResponse.success && statusResponse.data.isActive) {
					// User is already approved, go directly to game
					navigate(`/story/${storyId}`);
				} else {
					// User exists but is waiting for approval
					setUserId(existingUser._id);
					setWaitingApproval(true);
				}
			} else {
				// No existing user found, register a new one
				console.log("No existing user found. Registering new user with room:", roomNumber);
				// Register new user - we accept ANY room number without validation
				const registrationData = { 
					roomNumber,
					guestName,
					storyId: localStorage.getItem("storyId") // Include storyId from QR code
				};
				
				// Add hotelId if available
				if (localStorage.getItem("hotelId")) {
					registrationData.hotelId = localStorage.getItem("hotelId");
				}
				
				console.log("Sending registration data:", registrationData);
				const response = await registerUser(registrationData);
				
				if (!response.success) {
					throw new Error(response.message || "登録に失敗しました。");
				}
				
				console.log("Registration successful:", response);
				
				// Store user data in local storage for persistence
				localStorage.setItem("userId", response.data.userId);
				localStorage.setItem("roomNumber", roomNumber);
				localStorage.setItem("guestName", guestName);
				
				// Store hotelId if available
				if (hotelInfo) {
					localStorage.setItem("hotelId", hotelInfo.hotelId);
					localStorage.setItem("hotelName", hotelInfo.name || '');
				}
				
				// Set waiting state until admin approves
				setUserId(response.data.userId);
				setWaitingApproval(true);
			}
		} catch (err) {
			console.error("Registration error:", err);
			setError(err.message || "登録に失敗しました。もう一度お試しください。");
		} finally {
			setIsLoading(false);
		}
	};
	
	// Check approval status periodically if waiting
	useEffect(() => {
		if (waitingApproval && userId) {
			console.log(`Setting up status check interval for user: ${userId}`);
			
			const checkInterval = setInterval(async () => {
				try {
					console.log(`Checking approval status for user: ${userId}`);
					const response = await checkUserActiveStatus(userId);
					
					console.log(`Status check response:`, response);
					
					if (response.success && response.data) {
						console.log(`User active status: ${response.data.isActive}`);
						
						if (response.data.isActive) {
							// User has been approved, proceed to game
							console.log(`User ${userId} has been approved, proceeding to game`);
							clearInterval(checkInterval);
							
							// Get story ID from localStorage (from QR code)
							const storyId = localStorage.getItem("storyId");
							if (storyId) {
								navigate(`/story/${storyId}`);
							} else {
								setError("ストーリーIDが見つかりません。管理者に連絡してください。");
							}
						}
					} else {
						console.warn(`Failed to check status or invalid response:`, response);
					}
				} catch (err) {
					console.error(`Error checking approval status:`, err);
				}
			}, 5000); // Check every 5 seconds
			
			return () => clearInterval(checkInterval);
		}
	}, [waitingApproval, userId, navigate]);

	return (
		<div className="relative bg-cover bg-center bg-no-repeat max-w-[480px] mx-auto min-h-screen p-4 space-y-4 flex flex-col justify-center" style={{ backgroundImage: `url(${beachBackground})`, fontFamily: 'Hiragino' }}>
			<style dangerouslySetInnerHTML={{ __html: `
				.custom-scrollbar::-webkit-scrollbar {
					width: 6px;
				}
				.custom-scrollbar::-webkit-scrollbar-track {
					background: rgba(255, 255, 255, 0.3);
					border-radius: 3px;
				}
				.custom-scrollbar::-webkit-scrollbar-thumb {
					background: rgba(0, 0, 0, 0.3);
					border-radius: 3px;
				}
				.custom-scrollbar::-webkit-scrollbar-thumb:hover {
					background: rgba(0, 0, 0, 0.5);
				}
			`}} />

			{/* Logo */}
			<div className="flex flex-col text-center mb-4">
				<img
					src={logoImage}
					alt="ANA Intercontinental"
					className="h-[25px] w-auto object-contain"
				/>
				<h1 className="text-[#1B3D6E] text-md font-serif -tracking-wide">
					ANA
					<br />
					INTERCONTINENTAL®
				</h1>
				<p className="text-[#1B3D6E] text-sm -tracking-normal">
					MANZA BEACH RESORT
				</p>
			</div>

			{/* Title */}
			<div className="text-center w-full text-black flex items-center justify-center">
				{storyLoading ? (
					<div className="flex items-center justify-center min-h-[120px]">
						<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : currentStory ? (
					<div className="w-full">
						<h1 className="text-3xl leading-tight mb-3 font-medium tracking-wide">
							{currentStory.title}
						</h1>
						{currentStory.description && (
							<div className="max-h-[150px] overflow-y-auto px-4 custom-scrollbar">
								<p className="text-sm font-normal">
									{currentStory.description}
								</p>
							</div>
						)}
					</div>
				) : (
					<div className="min-h-[120px] flex items-center justify-center">
						<div>
							<h2 className="text-xl mb-2 font-normal">
								観光アンバサダー比嘉さんと&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
							</h2>
							<h1 className="text-4xl leading-none mb-2 font-medium tracking-widest">
								青い海の秘密
							</h1>
							<p className="text-md font-normal">
								〜沖縄の妖精きじむなーからの挑戦状〜
							</p>
						</div>
					</div>
				)}
			</div>

			{/* {First Image} */}
			<div className="flex items-center justify-center">
				<img src={animeCharacter} className="w-[180px]" alt="Character" />
			</div>

			{/* Hotel Information Card (if scanned) */}
			{hotelInfo && (
				<div className="bg-white/90 rounded-lg p-4 shadow-md mb-2 text-center border-2 border-blue-200">
					<h2 className="text-lg font-bold text-blue-800 mb-1">ホテル情報</h2>
					<div className="text-md font-medium text-gray-800">{hotelInfo.name}</div>
					{hotelInfo.address && (
						<div className="text-sm text-gray-600 mt-1">{hotelInfo.address}</div>
					)}
					<div className="mt-2 text-xs text-green-600 flex items-center justify-center">
						<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
						</svg>
						QRコードで接続済み
					</div>
				</div>
			)}

			{/* Waiting for Approval Message */}
			{waitingApproval && (
				<div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center">
					<h3 className="text-yellow-800 font-bold mb-2">承認待ち</h3>
					<p className="text-yellow-700 text-sm">
						登録が完了しました。管理者の承認をお待ちください。<br/>
						承認後、自動的にゲームが開始されます。
					</p>
					<div className="mt-3 flex justify-center">
						<div className="animate-pulse flex items-center space-x-2">
							<div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
							<div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
							<div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
						</div>
					</div>
				</div>
			)}

			{/* Login Form - Hide if waiting for approval */}
			{!waitingApproval && (
				<div className="space-y-4">
					<form onSubmit={handleSubmit} className="flex flex-col space-y-4">
						{/* Error display section */}
						{error && (
							<div className="text-red-500 text-center text-sm bg-red-100 p-2 rounded">
								{error}
							</div>
						)}
						
						<div className="flex flex-col">
							<div className="relative">
								<input
									type="text"
									placeholder="お部屋番号"
									className="bg-white/80 border border-gray-300 rounded-full p-3 text-center w-full"
									value={roomNumber}
									onChange={(e) => setRoomNumber(e.target.value)}
								/>
							</div>
						</div>
						<div className="flex flex-col">
							<div className="relative">
								<input
									type="text"
									placeholder="お名前"
									className="bg-white/80 border border-gray-300 rounded-full p-3 text-center w-full"
									value={guestName}
									onChange={(e) => setGuestName(e.target.value)}
								/>
							</div>
						</div>

						<button
							type="submit"
							disabled={isDisabled || isLoading}
							className={`bg-gradient-to-r from-[#7FD6E2] to-[#7FE7D3] text-white font-bold py-3 px-4 rounded-full 
								${
									isDisabled || isLoading
										? "opacity-50 cursor-not-allowed"
										: "hover:from-[#6FC6D2] hover:to-[#6FD7C3]"
								}`}
						>
							{isLoading ? "処理中..." : "登録する"}
						</button>
					</form>
				</div>
			)}


		</div>
	)
}

export default Home;